<script>
    import { preventDefault } from 'svelte/legacy';

import {
    toast } from '@zerodevx/svelte-toast';
import {
    modalState, hideModal, showModal
} from '@app/store/modalStore.svelte.js';
import {
    fade
} from 'svelte/transition';
import {
    Circle
} from 'svelte-loading-spinners';
import * as m from "@app/paraglide/messages.js";
/* Firebase */
import { functions } from '@app/configs/firebase.js';
import { httpsCallable } from 'firebase/functions';

let machineId = $state('');
let activationCode = $state('');
let legacyCode = $state('');
let loading = $state(false);
let errMsg = $state();

function copyActivationCode(){
    navigator.clipboard.writeText(activationCode);
    /* Notification */
    toast.push(m.offline_activation_copy_success_toast(), {classes: ['toast-`success`']});
}

async function generateActivationCode() {
    try {
        loading = true;
        errMsg = "";
        if (!machineId) throw new Error(m.offline_activation_missing_machine_id_error());
        let res = await httpsCallable(functions, 'v8GenerateOfflineActivationCode')(machineId);
        if (res.data.error) throw new Error(res.data.error);
        // Signed code for 1.8.51+; the legacy code still activates offline builds up to 1.8.50.
        activationCode = res.data.licenseToken || res.data.activationCode;
        legacyCode = res.data.licenseToken ? res.data.activationCode : "";
    } catch (err) {
        console.log(err, err.message);
        errMsg = err.message;
        /* Notification */
        toast.push(`${m.offline_activation_generate_failed_toast()} ${err.message}`, {classes: ['toast-`danger`']});
    } finally {
        loading = false;
    }
}
</script>

<div transition:fade={{duration: 100}} 
    class="modal {modalState.selected === 'offlineActivation' ? 'show d-block' : ''}" 
    role="dialog" 
    aria-labelledby="offlineActivationTitle"
    aria-describedby="offlineActivationDescription"
    tabindex="-1" 
    id="OfflineActivationModal">
    <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="offlineActivationTitle">{m.offline_activation_title()}</h4>
                <button type="button" class="btn-close" id="offlineActivationCloseBtn" aria-label={m.offline_activation_close_aria_label()} onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <p id="offlineActivationDescription">{m.offline_activation_description()}</p>
                <form onsubmit={preventDefault(()=> generateActivationCode())} aria-describedby="offlineActivationDescription">
                    <div class="mb-3">
                        <label for="machineIdInput" class="form-label">{m.offline_activation_machine_id_label()}</label>
                        <input placeholder={m.offline_activation_machine_id_placeholder()} 
                               type="text" 
                               class="form-control" 
                               id="machineIdInput" 
                               bind:value={machineId} 
                               disabled={loading}
                               aria-required="true"
                               aria-invalid={!machineId}/>
                    </div>
                    <div class="mb-3">
                        <label for="activationCodeInput" class="form-label">{m.offline_activation_code_label()}</label>
                        <input placeholder={m.offline_activation_code_placeholder()} 
                               type="text" 
                               class="form-control" 
                               id="activationCodeInput" 
                               bind:value={activationCode} 
                               onfocus={(e)=>{e.target.select()}} 
                               disabled={loading} 
                               readonly
                               aria-label={m.offline_activation_code_aria_label()}/>
                    </div>
                    {#if legacyCode}
                        <details class="mb-3 small">
                            <summary class="text-body-secondary">{m.offline_activation_legacy_summary()}</summary>
                            <input type="text" class="form-control form-control-sm mt-2" value={legacyCode} readonly
                                   onfocus={(e)=>{e.target.select()}} aria-label={m.offline_activation_legacy_summary()}/>
                        </details>
                    {/if}
                    {#if loading}
                        <div aria-live="polite">
                            <Circle size="30" color="#1eb4b2" unit="px" duration="1s"></Circle>
                            <p>{m.offline_activation_generating_status()}</p>
                        </div>
                    {/if}
                    {#if errMsg}
                        <p class="m-0 text-danger float-start" role="alert">{errMsg}</p>
                    {/if}
                </form>
            </div>
            <div class="modal-footer">
                <button id="copyCodeBtn"
                        disabled={loading || !activationCode} 
                        class="btn btn-dark" 
                        type="button" 
                        onclick={() => copyActivationCode()}
                        aria-label={m.offline_activation_copy_aria_label()}>
                    <i class="bi bi-files" aria-hidden="true"></i> {m.offline_activation_copy_button()}
                </button>
                <button id="generateCodeBtn"
                        class="btn btn-primary" 
                        type="button" 
                        onclick={generateActivationCode} 
                        disabled={loading}
                        aria-label={m.offline_activation_generate_aria_label()}>
                    {m.offline_activation_generate_button()}
                </button>
            </div>
        </div>
    </div>
</div>
